import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Eye, Calendar, User, Building2, FileText, ChevronUp, ZoomIn, ZoomOut, Download } from 'lucide-react'
import { NotFoundScreen } from '@/components/ui/NotFoundScreen'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { libraryService, LibraryDocument } from '@/services/libraryService'
import * as pdfjsLib from 'pdfjs-dist'

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url,
).toString()

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  artigo: 'Artigo Científico',
  manual: 'Manual Técnico',
  boletim: 'Boletim',
  relatorio: 'Relatório',
  tese: 'Tese/Dissertação',
  guia: 'Guia Prático',
}

function PdfPageCanvas({
  pdfDoc,
  pageNumber,
  scale,
}: {
  pdfDoc: pdfjsLib.PDFDocumentProxy
  pageNumber: number
  scale: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [rendered, setRendered] = useState(false)

  // Only render when page is near the viewport
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setRendered(true) },
      { rootMargin: '800px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!rendered) return
    let cancelled = false

    const renderPage = async () => {
      const page = await pdfDoc.getPage(pageNumber)
      if (cancelled || !canvasRef.current) return

      const viewport = page.getViewport({ scale })
      const canvas = canvasRef.current
      canvas.width = viewport.width
      canvas.height = viewport.height

      const ctx = canvas.getContext('2d')!
      await page.render({ canvasContext: ctx, canvas, viewport }).promise
    }

    renderPage()
    return () => { cancelled = true }
  }, [pdfDoc, pageNumber, scale, rendered])

  // Placeholder height keeps scroll position stable before rendering
  const placeholderHeight = Math.round(scale * 842)

  return (
    <div ref={wrapperRef} style={{ minHeight: rendered ? undefined : placeholderHeight }}>
      <canvas
        ref={canvasRef}
        className="block mx-auto"
        style={{ backgroundColor: 'white' }}
      />
    </div>
  )
}

export function LibraryDocumentScreen() {
  const { slug } = useParams<{ slug: string }>()
  const [doc, setDoc] = useState<LibraryDocument | null>(null)
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageInputValue, setPageInputValue] = useState('')
  const [isEditingPage, setIsEditingPage] = useState(false)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [fitScale, setFitScale] = useState(1)
  const [zoomPercent, setZoomPercent] = useState(100)
  const [downloading, setDownloading] = useState(false)
  const scale = fitScale * (zoomPercent / 100)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Load document metadata
  useEffect(() => {
    const loadDoc = async () => {
      if (!slug) return
      try {
        setLoading(true)
        setError(null)

        const docData = await libraryService.getDocumentBySlug(slug)
        setDoc(docData)

        // Track view
        let viewedDocs: Record<string, number> = {}
        try {
          viewedDocs = JSON.parse(localStorage.getItem('viewedLibDocs') || '{}')
        } catch {
          viewedDocs = {}
        }
        const lastViewed = viewedDocs[docData.id]
        const now = Date.now()
        const ONE_HOUR = 60 * 60 * 1000

        if (!lastViewed || (now - lastViewed) > ONE_HOUR) {
          viewedDocs[docData.id] = now
          localStorage.setItem('viewedLibDocs', JSON.stringify(viewedDocs))
          libraryService.incrementView(docData.id).catch(() => { })
        }
      } catch (err) {
        console.error('Error loading document:', err)
        setError('Erro ao carregar documento')
      } finally {
        setLoading(false)
      }
    }

    loadDoc()
  }, [slug])

  // Load PDF via authenticated fetch
  useEffect(() => {
    if (!doc) return
    let cancelled = false

    const loadPdf = async () => {
      setPdfLoading(true)
      try {
        const arrayBuffer = await libraryService.fetchPdfBlob(doc.id)
        if (cancelled) return

        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
        if (cancelled) return

        setPdfDoc(pdf)
        setTotalPages(pdf.numPages)
      } catch (err) {
        console.error('Error loading PDF:', err)
        setError('Erro ao carregar PDF')
      } finally {
        setPdfLoading(false)
      }
    }

    loadPdf()
    return () => { cancelled = true }
  }, [doc])

  // Calculate fit-to-width scale after PDF loads
  useEffect(() => {
    if (!pdfDoc || !containerRef.current) return
    pdfDoc.getPage(1).then(page => {
      const viewport = page.getViewport({ scale: 1 })
      const contentWidth = (contentRef.current?.clientWidth ?? 400) - 32
      setFitScale(contentWidth / viewport.width)
      setZoomPercent(100)
    })
  }, [pdfDoc])

  const handleDownload = useCallback(async () => {
    if (!doc || downloading) return
    setDownloading(true)
    try {
      const filename = doc.original_filename || `${doc.title}.pdf`
      await libraryService.downloadDocument(doc.id, filename)
    } catch (err) {
      console.error('Error downloading document:', err)
    } finally {
      setDownloading(false)
    }
  }, [doc, downloading])

  // Track scroll position for page indicator + scroll-to-top
  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container || totalPages === 0) return

    setShowScrollTop(container.scrollTop > 600)

    const scrollRatio = container.scrollTop / (container.scrollHeight - container.clientHeight || 1)
    const estimatedPage = Math.max(1, Math.min(
      totalPages,
      Math.ceil(scrollRatio * totalPages)
    ))
    setCurrentPage(estimatedPage)
  }, [totalPages])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const scrollToPage = useCallback((page: number) => {
    const container = containerRef.current
    const target = document.getElementById(`pdf-page-${page}`)
    if (target && container) {
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 16
      container.scrollTo({ top, behavior: 'smooth' })
    }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-bg">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-primary border-t-transparent" />
      </div>
    )
  }

  if (error || !doc) {
    return <NotFoundScreen type="post" />
  }

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-brand-bg pb-20 lg:pb-0 overflow-y-auto h-full"
    >
      <div ref={contentRef} className="max-w-4xl mx-auto px-4 pt-6 pb-0">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Biblioteca', href: '/biblioteca' },
            { label: doc.title },
          ]}
        />

        {/* Document info header */}
        <div className="card p-6 md:p-8 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-brand-text-muted">
              {doc.taxonomies?.find(t => t.type === 'library_category') && (
                <span className="px-3 py-1 bg-brand-primary-light text-brand-primary rounded-lg font-medium text-xs">
                  {doc.taxonomies.find(t => t.type === 'library_category')!.name}
                </span>
              )}
              <span className="px-2 py-0.5 bg-brand-surface-alt text-brand-text-muted rounded-md text-xs font-medium">
                {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
              </span>
            </div>

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 btn-primary disabled:opacity-60"
            >
              <Download size={20} />
              {downloading ? 'Baixando...' : 'Baixar PDF'}
            </button>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-brand-text mb-3">
            {doc.title}
          </h1>

          {doc.summary && (
            <p className="text-brand-text-secondary mb-4 leading-relaxed">
              {doc.summary}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-brand-text-muted">
            {doc.author_name && (
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {doc.author_name}
              </span>
            )}
            {doc.source && (
              <span className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {doc.source}
              </span>
            )}
            {doc.publication_year && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {doc.publication_year}
              </span>
            )}
            {totalPages > 0 && (
              <span className="flex items-center gap-1.5">
                <FileText className="w-4 h-4" />
                {totalPages} páginas
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Eye className="w-4 h-4" />
              {doc.view_count} visualizações
            </span>
          </div>

          {/* Tags */}
          {doc.taxonomies && doc.taxonomies.filter(t => t.type === 'library_tag').length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-brand-border">
              {doc.taxonomies.filter(t => t.type === 'library_tag').map(tag => (
                <span
                  key={tag.id}
                  className="px-2.5 py-1 bg-brand-surface-alt text-brand-text-secondary rounded-lg text-xs"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* PDF Viewer */}
        {pdfLoading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-t-transparent" />
            <p className="text-brand-text-muted text-sm">Carregando documento...</p>
          </div>
        )}
      </div>

      {/* PDF pages outside max-w-4xl so flex centering works at any zoom level */}
      {pdfDoc && (
        <div className="flex justify-center px-4 pb-2">
          <div className="space-y-2 shrink-0">
            {Array.from({ length: totalPages }, (_, i) => (
              <div key={i} id={`pdf-page-${i + 1}`} className="bg-white rounded-lg shadow-sm">
                <PdfPageCanvas pdfDoc={pdfDoc} pageNumber={i + 1} scale={scale} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* End indicator */}
      {pdfDoc && totalPages > 0 && (
        <div className="text-center py-6">
          <p className="text-brand-text-muted text-sm">Fim do documento</p>
        </div>
      )}

      {/* Floating controls */}
      {totalPages > 0 && (
        <div className="fixed bottom-24 lg:bottom-6 right-4 flex flex-col items-end gap-2 z-30">
          {showScrollTop && (
            <button
              onClick={scrollToTop}
              className="p-2.5 bg-brand-surface shadow-lg rounded-full text-brand-text-muted hover:text-brand-primary transition-colors border border-brand-border"
            >
              <ChevronUp className="w-5 h-5" />
            </button>
          )}

          {/* Zoom controls */}
          <div className="flex items-center gap-1 bg-brand-surface shadow-lg rounded-full border border-brand-border">
            <button
              onClick={() => setZoomPercent(p => Math.max(50, p - 25))}
              className="p-2 text-brand-text-muted hover:text-brand-primary transition-colors"
              title="Diminuir zoom"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs font-medium text-brand-text-secondary min-w-[3rem] text-center">
              {zoomPercent}%
            </span>
            <button
              onClick={() => setZoomPercent(p => Math.min(300, p + 25))}
              className="p-2 text-brand-text-muted hover:text-brand-primary transition-colors"
              title="Aumentar zoom"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          <div className="bg-brand-surface shadow-lg rounded-full px-3 py-1.5 text-xs font-medium text-brand-text-secondary border border-brand-border flex items-center gap-1">
            <input
              type="text"
              inputMode="numeric"
              value={isEditingPage ? pageInputValue : String(currentPage)}
              onFocus={() => { setIsEditingPage(true); setPageInputValue(String(currentPage)) }}
              onChange={e => setPageInputValue(e.target.value)}
              onBlur={() => setIsEditingPage(false)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const page = parseInt(pageInputValue)
                  if (!isNaN(page) && page >= 1 && page <= totalPages) scrollToPage(page)
                  setIsEditingPage(false)
                  e.currentTarget.blur()
                }
                if (e.key === 'Escape') { setIsEditingPage(false); e.currentTarget.blur() }
              }}
              className="w-8 text-center bg-transparent outline-none"
            />
            <span>/ {totalPages}</span>
          </div>
        </div>
      )}
    </div>
  )
}
